import { Component, computed, inject, signal, ViewChild, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { OuiLayoutModule } from 'omnium-ui/layout';
import { OuiNavButton } from 'omnium-ui/layout';
import { AuthService } from '@lib/auth/auth.service';
import { MenuNode } from 'omnium-ui/shared';
import { OuiLoading } from 'omnium-ui/loading';
import { OuiIconModule } from 'omnium-ui/icon';
import { FicheConsultantPanelComponent } from '@lib/components/fiche-consultant-panel/fiche-consultant-panel.component';
import { OuiDialogService } from 'omnium-ui/dialog';
import { DemandeResiliationConsultantDialogComponent } from './resiliation/demande-resiliation-consultant-dialog/demande-resiliation-consultant-dialog.component';
import { ResiliationConsultantService } from './resiliation/services/resiliation-consultant.service';
import { GlobalErrorPageComponent } from '../../../front-gestion-reseau-common/src/lib/components/global-error-page/global-error-page.component';
import {
  OuiImportantMessage,
  OuiImportantMessageItem,
} from 'omnium-ui/important-message';
import { RenouvellementService } from './_shared/services/renouvellement/renouvellement.service';
import moment from 'moment';
import { RenouvellementDialogComponent } from './accueil/components/renouvellement-dialog/renouvellement-dialog.component';
import { AnnulationResiliationDialogComponent } from './resiliation/annulation-resiliation-dialog/annulation-resiliation-dialog.component';
import { CommissionAdiDialogComponent } from './accueil/components/commission-adi-dialog/commission-adi-dialog.component';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    OuiLayoutModule,
    OuiNavButton,
    OuiLoading,
    OuiIconModule,
    OuiImportantMessage,
    OuiImportantMessageItem,
    FicheConsultantPanelComponent,
    GlobalErrorPageComponent,
  ],
  providers: [OuiDialogService],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private readonly resiliationService = inject(ResiliationConsultantService);
  private readonly authService = inject(AuthService);
  private readonly dialogService = inject(OuiDialogService);
  private readonly renouvellementService = inject(RenouvellementService);

  @ViewChild(FicheConsultantPanelComponent)
  ficheConsultantPanel?: FicheConsultantPanelComponent;
  @ViewChild(GlobalErrorPageComponent)
  globalErrorPageComponent?: GlobalErrorPageComponent;

  readonly isLoading = signal(false);
  readonly isLoggedIn = computed<boolean>(() => !!this.authService.userInfo());

  readonly userName = computed<string>(() => {
    const userInfo = this.authService.userInfo();
    return userInfo ? `${userInfo.givenName} ${userInfo.sn}` : '';
  });

  readonly codeConsultant = computed<string | undefined>(
    () => this.authService.userInfo()?.id
  );

  readonly isResiliation = computed<boolean>(
    () => !!this.resiliationService.resiliation()
  );

  readonly aideEnLigneLink: MenuNode = {
    name: 'help',
    href: 'https://prodemial.zendesk.com/hc/fr/categories/360001045591-Cosmos-et-ses-outils',
  };

  readonly menu: MenuNode[] = [
    { name: 'Habilitations', routeLink: '/habilitations' },
    { name: 'Affiliations', routeLink: '/affiliations' },
    { name: 'Mes documents', routeLink: '/documents' },
  ];

  readonly userMenu: MenuNode[] = [
    {
      name: 'Ma fiche consultant',
      action: () => {
        this.ficheConsultantPanel?.open(this.codeConsultant()!);
      },
    },
    {
      name: 'Se déconnecter',
      action: () => {
        this.authService.logout();
      },
    },
  ];

  readonly isEligibleRenouvellement = this.renouvellementService.isEligibleRenouvellement;
  readonly isEligibleCommissionAdi = this.renouvellementService.isEligibleCommissionAdi;
  readonly actuelCommissionAdi = this.renouvellementService.actuelCommissionAdi;
  readonly resiliation = this.resiliationService.resiliation;
  readonly displayImportantMessage = computed(
    () =>
      this.isEligibleRenouvellement() ||
      this.isEligibleCommissionAdi() ||
      !!this.resiliationMessage()
  );

  readonly resiliationMessage = computed<string | undefined>(() => {
    const resiliation = this.resiliation();

    if (!resiliation) {
      return undefined;
    }

    if (resiliation.statutConsultant == 'a_signer') {
      return "Il ne manque qu'une étape pour confirmer votre demande dé résiliation : pensez à signer le mail reçu afin que nous puissions la transmettre à nos équipes.";
    } else if (resiliation.statutConsultant == 'en_attente') {
      return 'Votre demande de résiliation est actuellement en cours de traitement par nos services.';
    } else if (resiliation.statutConsultant == 'en_cours') {
      if (resiliation.dureePreavis === null) {
        return 'Votre résiliation est en cours.';
      }

      const today = moment().startOf('day');
      const dateEnd = moment(resiliation.dateEffet).startOf('day');

      const remainingDays = dateEnd.diff(today, 'days') + 1;

      if (remainingDays < 1) {
        return 'Votre résiliation est en cours.';
      }

      return `Votre résiliation est en cours : il vous reste encore ${remainingDays} jour${remainingDays > 1 ? 's' : ''} avant la fermeture de votre adhésion.`;
    }

    return undefined;
  });

  constructor() {
    const user = this.authService.login();
    this.resiliationService.getResiliation();

    setTimeout(() => {
      if (!this.authService.userInfo() && user) {
        this.isLoading.set(true);
      }
      if (!this.isLoading() && !user) {
        this.globalErrorPageComponent?.open();
      }
    }, 100);
  }

  ngOnInit(): void {
    Promise.all([
      this.renouvellementService.getIsEligibleRenouvellement(),
      this.renouvellementService.getIsEligibleCommissionAdi(),
    ]).then(([isEligibleRen, isEligibleComAdi]) => {
      if (isEligibleRen) {
        this.openRenouvellementDialog();
      } else if (isEligibleComAdi) {
        this.openRenouvellementComAdiDialog();
      }
    });
  }

  openResiliationDialog() {
    this.ficheConsultantPanel?.closeSlidePanel();
    this.dialogService.openDialog(DemandeResiliationConsultantDialogComponent);
  }

  openRenouvellementDialog() {
    this.dialogService.openDialog(RenouvellementDialogComponent);
  }

  openRenouvellementComAdiDialog() {
    this.dialogService.openDialog(CommissionAdiDialogComponent, {
      actuelCommissionAdi: this.renouvellementService.actuelCommissionAdi(),
    });
  }

  openCancelResiliationDialog() {
    this.dialogService.openDialog(AnnulationResiliationDialogComponent);
  }
}
